import type { DiscountApi } from "../../api/DiscountApi";
import type { CatalogDiscountCategoryFetchOptions } from "../../generated/dto/CatalogDiscountCategoryFetchOptions";
import type { CatalogDiscountCategoryStaff } from "../../generated/dto/CatalogDiscountCategoryStaff";
import type { CatalogDiscountFetchOptions } from "../../generated/dto/CatalogDiscountFetchOptions";
import type { CatalogDiscountInstance } from "../../generated/dto/CatalogDiscountInstance";
import type { CatalogDiscountInstanceFetchOptions } from "../../generated/dto/CatalogDiscountInstanceFetchOptions";
import type { CatalogDiscountInstanceStaff } from "../../generated/dto/CatalogDiscountInstanceStaff";
import type { CatalogDiscountSearchParams } from "../../generated/dto/CatalogDiscountSearchParams";
import type { CatalogDiscountStaff } from "../../generated/dto/CatalogDiscountStaff";
import type { CatalogDiscountStaffSearchResult } from "../../generated/dto/CatalogDiscountStaffSearchResult";
import type { CatalogFetchOptions } from "../../generated/dto/CatalogFetchOptions";
import type { CatalogItemDiscountFetchOptions } from "../../generated/dto/CatalogItemDiscountFetchOptions";
import type { CatalogItemDiscountStaff } from "../../generated/dto/CatalogItemDiscountStaff";
import type { CatalogItemFetchOptions } from "../../generated/dto/CatalogItemFetchOptions";
import type { CatalogItemStaff } from "../../generated/dto/CatalogItemStaff";
import type { CatalogStaff } from "../../generated/dto/CatalogStaff";
import type { CreateCatalogDiscount } from "../../generated/dto/CreateCatalogDiscount";
import type { CreateCatalogDiscountCategory } from "../../generated/dto/CreateCatalogDiscountCategory";
import type { CreateCatalogDiscountInstance } from "../../generated/dto/CreateCatalogDiscountInstance";
import type { CreateCatalogItemDiscount } from "../../generated/dto/CreateCatalogItemDiscount";
import type { DeleteCatalogDiscountCategoryStaff } from "../../generated/dto/DeleteCatalogDiscountCategoryStaff";
import type { DeleteCatalogDiscountInstanceStaff } from "../../generated/dto/DeleteCatalogDiscountInstanceStaff";
import type { DeleteCatalogDiscountStaff } from "../../generated/dto/DeleteCatalogDiscountStaff";
import type { DeleteCatalogItemDiscountStaff } from "../../generated/dto/DeleteCatalogItemDiscountStaff";
import type { DiscountVersionChangeRequest } from "../../generated/dto/DiscountVersionChangeRequest";
import type { UpdateCatalogDiscount } from "../../generated/dto/UpdateCatalogDiscount";
import type { UpdateCatalogDiscountCategory } from "../../generated/dto/UpdateCatalogDiscountCategory";
import type { UpdateCatalogDiscountInstance } from "../../generated/dto/UpdateCatalogDiscountInstance";
import type { UpdateCatalogItemDiscount } from "../../generated/dto/UpdateCatalogItemDiscount";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class DiscountFrontendService {
  constructor(private readonly api: DiscountApi) {}
  private readonly catalogDiscountCategoryStaffCache: Map<number, CatalogDiscountCategoryStaff> = new Map();
  private readonly catalogItemDiscountStaffCache: Map<number, CatalogItemDiscountStaff> = new Map();
  private readonly catalogDiscountStaffCache: Map<number, CatalogDiscountStaff> = new Map();
  private readonly catalogDiscountInstanceStaffCache: Map<number, CatalogDiscountInstanceStaff> = new Map();
  async getCatalogDiscountCategoryStaff(id: number, options?: CatalogDiscountCategoryFetchOptions): Promise<CatalogDiscountCategoryStaff> {
    const __cached = this.catalogDiscountCategoryStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCatalogDiscountCategoryStaff(id, options);
    this.catalogDiscountCategoryStaffCache.set(id, __value);
    return __value
  }
  async getCatalogItemDiscountStaff(id: number, options?: CatalogItemDiscountFetchOptions): Promise<CatalogItemDiscountStaff> {
    const __cached = this.catalogItemDiscountStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCatalogItemDiscountStaff(id, options);
    this.catalogItemDiscountStaffCache.set(id, __value);
    return __value
  }
  async getCatalogDiscountStaff(id: number, options?: CatalogDiscountFetchOptions): Promise<CatalogDiscountStaff> {
    const __cached = this.catalogDiscountStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCatalogDiscountStaff(id, options);
    this.catalogDiscountStaffCache.set(id, __value);
    return __value
  }
  async getCatalogDiscountInstanceStaff(id: number, options?: CatalogDiscountInstanceFetchOptions): Promise<CatalogDiscountInstanceStaff> {
    const __cached = this.catalogDiscountInstanceStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCatalogDiscountInstanceStaff(id, options);
    this.catalogDiscountInstanceStaffCache.set(id, __value);
    return __value
  }
  async getAllCatalogDiscountCategoryStaffs(options?: CatalogDiscountCategoryFetchOptions): Promise<Array<CatalogDiscountCategoryStaff>> {
    return this.api.getAllCatalogDiscountCategoryStaffs(options)
  }
  async getAllCatalogItemDiscountStaffs(options?: CatalogItemDiscountFetchOptions): Promise<Array<CatalogItemDiscountStaff>> {
    return this.api.getAllCatalogItemDiscountStaffs(options)
  }
  async getAllCatalogDiscountStaffs(options?: CatalogDiscountFetchOptions): Promise<Array<CatalogDiscountStaff>> {
    return this.api.getAllCatalogDiscountStaffs(options)
  }
  async getAllCatalogDiscountInstanceStaffs(options?: CatalogDiscountInstanceFetchOptions): Promise<Array<CatalogDiscountInstanceStaff>> {
    return this.api.getAllCatalogDiscountInstanceStaffs(options)
  }
  async createCatalogDiscountCategoryStaff(source: CreateCatalogDiscountCategory): Promise<CatalogDiscountCategoryStaff> {
    const __result = await this.api.createCatalogDiscountCategoryStaff(source);
    this.catalogDiscountCategoryStaffCache.clear();
    return __result
  }
  async createCatalogItemDiscountStaff(source: CreateCatalogItemDiscount): Promise<CatalogItemDiscountStaff> {
    const __result = await this.api.createCatalogItemDiscountStaff(source);
    this.catalogItemDiscountStaffCache.clear();
    return __result
  }
  async createCatalogDiscountStaff(source: CreateCatalogDiscount): Promise<CatalogDiscountStaff> {
    const __result = await this.api.createCatalogDiscountStaff(source);
    this.catalogDiscountStaffCache.clear();
    return __result
  }
  async createCatalogDiscountInstanceStaff(source: CreateCatalogDiscountInstance): Promise<CatalogDiscountInstanceStaff> {
    const __result = await this.api.createCatalogDiscountInstanceStaff(source);
    this.catalogDiscountInstanceStaffCache.clear();
    return __result
  }
  async updateCatalogDiscountCategoryStaff(source: UpdateCatalogDiscountCategory): Promise<CatalogDiscountCategoryStaff> {
    const __result = await this.api.updateCatalogDiscountCategoryStaff(source);
    this.catalogDiscountCategoryStaffCache.clear();
    return __result
  }
  async updateCatalogItemDiscountStaff(source: UpdateCatalogItemDiscount): Promise<CatalogItemDiscountStaff> {
    const __result = await this.api.updateCatalogItemDiscountStaff(source);
    this.catalogItemDiscountStaffCache.clear();
    return __result
  }
  async updateCatalogDiscountStaff(source: UpdateCatalogDiscount): Promise<CatalogDiscountStaff> {
    const __result = await this.api.updateCatalogDiscountStaff(source);
    this.catalogDiscountStaffCache.clear();
    return __result
  }
  async updateCatalogDiscountInstanceStaff(source: UpdateCatalogDiscountInstance): Promise<CatalogDiscountInstanceStaff> {
    const __result = await this.api.updateCatalogDiscountInstanceStaff(source);
    this.catalogDiscountInstanceStaffCache.clear();
    return __result
  }
  async deleteCatalogDiscountCategoryStaff(source: DeleteCatalogDiscountCategoryStaff): Promise<void> {
    const __result = await this.api.deleteCatalogDiscountCategoryStaff(source);
    this.catalogDiscountCategoryStaffCache.clear();
    return __result
  }
  async deleteCatalogItemDiscountStaff(source: DeleteCatalogItemDiscountStaff): Promise<void> {
    const __result = await this.api.deleteCatalogItemDiscountStaff(source);
    this.catalogItemDiscountStaffCache.clear();
    return __result
  }
  async deleteCatalogDiscountStaff(source: DeleteCatalogDiscountStaff): Promise<void> {
    const __result = await this.api.deleteCatalogDiscountStaff(source);
    this.catalogDiscountStaffCache.clear();
    return __result
  }
  async deleteCatalogDiscountInstanceStaff(source: DeleteCatalogDiscountInstanceStaff): Promise<void> {
    const __result = await this.api.deleteCatalogDiscountInstanceStaff(source);
    this.catalogDiscountInstanceStaffCache.clear();
    return __result
  }
  async searchCatalogDiscountStaff(params: CatalogDiscountSearchParams, page: number, perPage: number): Promise<PaginationResult<CatalogDiscountStaffSearchResult>> {
    return this.api.searchCatalogDiscountStaff(params, page, perPage)
  }
  async searchCatalogDiscountStaffFull(params: CatalogDiscountSearchParams, page: number, perPage: number, options?: CatalogDiscountFetchOptions): Promise<PaginationResult<CatalogDiscountStaff>> {
    return this.api.searchCatalogDiscountStaffFull(params, page, perPage, options)
  }
  async getCatalogDiscountCategoryStaffDiscounts(id: number, options?: CatalogDiscountFetchOptions): Promise<Array<CatalogDiscountStaff>> {
    return this.api.getCatalogDiscountCategoryStaffDiscounts(id, options)
  }
  async getCatalogItemDiscountStaffItemId(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    return this.api.getCatalogItemDiscountStaffItemId(id, options)
  }
  async getCatalogItemDiscountStaffDiscountId(id: number, options?: CatalogDiscountFetchOptions): Promise<CatalogDiscountStaff> {
    return this.api.getCatalogItemDiscountStaffDiscountId(id, options)
  }
  async getCatalogDiscountStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff | null> {
    return this.api.getCatalogDiscountStaffCatalogId(id, options)
  }
  async getCatalogDiscountStaffCategoryId(id: number, options?: CatalogDiscountCategoryFetchOptions): Promise<CatalogDiscountCategoryStaff | null> {
    return this.api.getCatalogDiscountStaffCategoryId(id, options)
  }
  async getCatalogDiscountStaffDiscountInstances(id: number, options?: CatalogDiscountInstanceFetchOptions): Promise<Array<CatalogDiscountInstanceStaff>> {
    return this.api.getCatalogDiscountStaffDiscountInstances(id, options)
  }
  async getCatalogDiscountInstanceStaffDiscountId(id: number, options?: CatalogDiscountFetchOptions): Promise<CatalogDiscountStaff> {
    return this.api.getCatalogDiscountInstanceStaffDiscountId(id, options)
  }
  async changeDiscountVersionStaff(request: DiscountVersionChangeRequest): Promise<CatalogDiscountInstance> {
    return this.api.changeDiscountVersionStaff(request)
  }
}
